import { useEffect, useState } from "react";
import { api } from "./api";
import { OrgId } from "../types";
import type { KnowledgeBaseStatus } from "../types";
import { isOrg } from "./routes";
import { useSseEvent } from "./useSseEvent";

// Which orgs to showcase in the left menu, served by GET /api/orgs — the orgs that
// have knowledge-base content on disk (empty until the Incode-owned KB is synced
// with INCODE_GH_TOKEN). Fetched once and cached at module scope so every consumer
// (the sidebar selector + App's org gate) shares one request and one answer, then
// refetched live when the knowledge base changes (a sync can add/remove the
// system/org/<org> content dirs that decide which orgs appear).

export interface ShowcaseOrgs {
  /** Orgs to surface, in canonical order. */
  orgs: OrgId[];
  /** False until the fetch resolves (so callers don't act on an empty default). */
  loaded: boolean;
}

const EMPTY: ShowcaseOrgs = { orgs: [], loaded: false };

let cache: ShowcaseOrgs | null = null;
let inflight: Promise<ShowcaseOrgs> | null = null;
// Every mounted useOrgs() consumer, so a live refetch updates them all at once.
const subscribers = new Set<(s: ShowcaseOrgs) => void>();

async function fetchOrgs(): Promise<ShowcaseOrgs> {
  const r = await api.getOrgs();
  const orgs = (Array.isArray(r?.orgs) ? r.orgs : []).filter(isOrg);
  return { orgs, loaded: true };
}

function load(): Promise<ShowcaseOrgs> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetchOrgs()
      .then((s) => (cache = s))
      .catch(() => {
        inflight = null; // allow a later retry
        return EMPTY;
      });
  }
  return inflight;
}

function sameOrgs(a: ShowcaseOrgs, b: ShowcaseOrgs): boolean {
  return a.loaded === b.loaded && a.orgs.length === b.orgs.length && a.orgs.every((o, i) => o === b.orgs[i]);
}

// Refetch bypassing the cache and notify every consumer. Concurrent calls coalesce;
// a no-op change (same org set) doesn't churn subscribers. Keeps the previous cache
// on a failed refetch rather than blanking the menu.
let reloadInflight: Promise<void> | null = null;
function reload(): Promise<void> {
  if (reloadInflight) return reloadInflight;
  reloadInflight = fetchOrgs()
    .then((next) => {
      inflight = Promise.resolve(next);
      if (cache && sameOrgs(cache, next)) return;
      cache = next;
      for (const notify of subscribers) notify(next);
    })
    .catch(() => {
      /* transient error — keep the current cache */
    })
    .finally(() => {
      reloadInflight = null;
    });
  return reloadInflight;
}

export function useOrgs(): ShowcaseOrgs {
  const [state, setState] = useState<ShowcaseOrgs>(cache ?? EMPTY);
  useEffect(() => {
    let cancelled = false;
    subscribers.add(setState);
    if (cache) setState(cache);
    else void load().then((s) => { if (!cancelled) setState(s); });
    return () => { cancelled = true; subscribers.delete(setState); };
  }, []);

  // A knowledge-base sync can materialize an org's content dirs, so the menu must
  // follow. Refetch once a sync settles into "ready" (content is now on disk); the
  // sameOrgs guard makes it a no-op when the org set didn't actually change.
  useSseEvent<KnowledgeBaseStatus>("knowledge_base_update", (s) => {
    if (s.phase === "ready") void reload();
  });

  return state;
}
